import { CheckCircle2, Clock, Loader2, LogOut } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useClockIn } from "../../hooks/use-clock-in";
import { useClockOut } from "../../hooks/use-clock-out";
import { useGeolocation } from "../../hooks/use-geolocation";
import type { TimeEntry } from "../../types";
import { hasOpenEntry, latestEntry } from "../../utils/day";
import { EMPTY_VALUE, formatClock } from "../../utils/format";
import { IntervalLocation } from "../interval-location";

type PunchState = "open" | "closed" | "disabled" | "not-member";

const STATE_COPY: Record<PunchState, string> = {
	open: "Você está com o expediente aberto. Registre a saída ao terminar o dia.",
	closed: "Registre a entrada ao chegar no canteiro.",
	disabled:
		"O bate ponto está desativado para você nesta obra. Peça a um administrador para ativá-lo.",
	"not-member":
		"Você não é membro deste canteiro. Peça a um administrador para incluir você na equipe.",
};

function punchState(
	isMember: boolean,
	timeClockEnabled: boolean,
	isOpen: boolean,
): PunchState {
	if (!isMember) return "not-member";
	if (!timeClockEnabled) return "disabled";
	return isOpen ? "open" : "closed";
}

export function PunchCard({
	entries,
	isMember,
	timeClockEnabled,
	canClock,
}: {
	entries: TimeEntry[];
	isMember: boolean;
	timeClockEnabled: boolean;
	canClock: boolean;
}) {
	const clockIn = useClockIn();
	const clockOut = useClockOut();
	const requestLocation = useGeolocation();
	const [locating, setLocating] = useState(false);

	const isOpen = hasOpenEntry(entries);
	const entry = latestEntry(entries);
	const state = punchState(isMember, timeClockEnabled, isOpen);
	const isPending = locating || clockIn.isPending || clockOut.isPending;

	async function punch(mutate: typeof clockIn.mutate) {
		setLocating(true);
		try {
			const location = await requestLocation();
			mutate(location);
		} finally {
			setLocating(false);
		}
	}

	return (
		<Card className="punch-card" data-state={state}>
			<div className="punch-card__strip" data-state={state} />
			<CardContent className="punch-card__content">
				<div className="punch-card__info">
					<Badge variant={isOpen ? "success" : "secondary"}>
						{isOpen ? <CheckCircle2 /> : <Clock />}
						{isOpen ? "Expediente ativo" : "Fora do expediente"}
					</Badge>
					<div className="punch-card__times">
						<span className="punch-card__time">
							{formatClock(entry?.clockedInAt)}
						</span>
						<span className="punch-card__arrow">→</span>
						<span className="punch-card__time-out">
							{entry?.clockedOutAt
								? formatClock(entry.clockedOutAt)
								: EMPTY_VALUE}
						</span>
					</div>
					{entry && (
						<div className="punch-card__location">
							<IntervalLocation entry={entry} />
						</div>
					)}
					{entries.length > 1 && (
						<ul
							className="punch-card__intervals"
							aria-label="Intervalos de hoje"
						>
							{entries.map((interval) => (
								<li key={interval.id}>
									<span>{formatClock(interval.clockedInAt)}</span>
									<span className="punch-card__arrow">→</span>
									<span>
										{interval.clockedOutAt
											? formatClock(interval.clockedOutAt)
											: "em curso"}
									</span>
									<IntervalLocation entry={interval} />
								</li>
							))}
						</ul>
					)}
					<p className="punch-card__hint">{STATE_COPY[state]}</p>
				</div>

				{canClock && isMember && (
					<div className="punch-card__actions">
						{isOpen ? (
							<Button
								size="lg"
								variant="destructive"
								disabled={!timeClockEnabled || isPending}
								onClick={() => punch(clockOut.mutate)}
							>
								{isPending ? (
									<Loader2 className="punch-card__spinner" />
								) : (
									<LogOut />
								)}
								{locating ? "Obtendo localização…" : "Bater saída agora"}
							</Button>
						) : (
							<Button
								size="lg"
								disabled={!timeClockEnabled || isPending}
								onClick={() => punch(clockIn.mutate)}
							>
								{isPending ? (
									<Loader2 className="punch-card__spinner" />
								) : (
									<CheckCircle2 />
								)}
								{locating ? "Obtendo localização…" : "Bater entrada agora"}
							</Button>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

export function PunchCardSkeleton() {
	return (
		<Card className="punch-card">
			<div className="punch-card__strip" />
			<CardContent className="punch-card__content">
				<div className="punch-card__info">
					<Skeleton className="punch-card__skeleton-badge" />
					<Skeleton className="punch-card__skeleton-times" />
					<Skeleton className="punch-card__skeleton-hint" />
				</div>
				<Skeleton className="punch-card__skeleton-action" />
			</CardContent>
		</Card>
	);
}
